/**
 * Bounded map. Clearing on overflow instead makes a cache that is worse than
 * none: a route whose keys exceed the cap wipes on every render, every marker
 * gets a new icon identity, and Leaflet rebuilds the element.
 */
export class BoundedCache<V> {
  private readonly entries = new Map<string, V>();

  /** `lru` costs a delete+set on every hit, so it is opt-in: only worth it where
   *  the key space can actually reach the cap. */
  constructor(
    private readonly max: number,
    private readonly lru = false,
  ) {}

  get(key: string): V | undefined {
    const value = this.entries.get(key);
    if (value === undefined || !this.lru) return value;
    this.entries.delete(key);
    this.entries.set(key, value);
    return value;
  }

  set(key: string, value: V): V {
    this.entries.delete(key);
    this.entries.set(key, value);
    if (this.entries.size > this.max) {
      const oldest = this.entries.keys().next();
      if (!oldest.done) this.entries.delete(oldest.value);
    }
    return value;
  }

  get size(): number {
    return this.entries.size;
  }
}
